import {
  amazonConnectApi,
  type CommerceSyncHealth,
  type PricingRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import { fetchInventorySample } from '../daily-brief/loadDailyBrief';
import { buildBusinessHealth } from '../daily-brief/healthScore';
import {
  dayAggMap,
  fetchOrdersMetricsSeries,
  windowFromMetrics,
  windowFromSeriesDays,
} from '../ordersTruth';
import type { CompareMode, DatePreset } from '../types';
import { buildExecutiveKpis } from './buildKpis';
import { compareWindow, periodWindow, trailingDayWindows } from './periodWindows';
import type { ExecutiveKpiStripModel } from './types';

function staleHours(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / 3_600_000;
}

export async function loadExecutiveKpis(opts: {
  connection: SellerConnection | null;
  health: CommerceSyncHealth | null;
  datePreset: DatePreset;
  compare: CompareMode;
}): Promise<ExecutiveKpiStripModel> {
  const current = periodWindow(opts.datePreset);
  const prior = compareWindow(current, opts.compare);
  const sparkDays = trailingDayWindows(current.end, 7);
  const conn = opts.connection;
  const connectionId = conn?.id ?? null;

  const emptyHealth = {
    overall: null as number | null,
    band: 'unavailable' as const,
    componentsReady: 0,
    componentsTotal: 6,
    components: [],
    confidence: 0,
    confidenceLabel: 'low' as const,
  };

  if (!connectionId) {
    return buildExecutiveKpis({
      connectionId: null,
      current,
      prior,
      orders: [],
      inventory: [],
      pricing: [],
      health: emptyHealth,
      sparkDays,
      truncated: false,
      periodLabel: current.label,
      priorLabel: prior?.label ?? 'No compare',
      error: null,
    });
  }

  const ordersRow = opts.health?.domains?.find(
    (d) => d.marketsync_domain === 'orders' || d.key === 'orders',
  );
  const lastSync = conn.last_sync_at || ordersRow?.last_sync_at || null;

  let error: string | null = null;
  let inventory = { rows: [] as Awaited<ReturnType<typeof fetchInventorySample>>['rows'], total: null as number | null, zero: 0 };
  let pricing: PricingRow[] = [];
  let currentAgg = windowFromMetrics({
    connection_id: connectionId,
    from: current.isoFrom,
    to: current.isoTo,
    timezone: 'Asia/Kolkata',
    sales: 0,
    order_items: 0,
    units: 0,
    complete: true,
  });
  let priorAgg = prior ? { ...currentAgg } : undefined;
  let sparkAggs = sparkDays.map(() => currentAgg);
  let moneySource: string | undefined;
  let incomplete = false;

  try {
    const sparkFrom = sparkDays[0]?.isoFrom ?? current.isoFrom;
    const currentFrom = sparkFrom < current.isoFrom ? sparkFrom : current.isoFrom;
    const [currentSeries, priorSeries, inv, pricingPage] = await Promise.all([
      fetchOrdersMetricsSeries(connectionId, {
        from: currentFrom,
        to: current.isoTo,
        grain: 'day',
      }),
      prior
        ? fetchOrdersMetricsSeries(connectionId, {
            from: prior.isoFrom,
            to: prior.isoTo,
            grain: 'day',
          })
        : Promise.resolve(null),
      fetchInventorySample(connectionId).catch(() => ({
        rows: [],
        total: null,
        zero: 0,
      })),
      amazonConnectApi
        .listPricingPage(connectionId, { page: 1, page_size: 100 })
        .catch(() => null),
    ]);
    const buckets = [
      ...(currentSeries.buckets ?? []),
      ...(priorSeries?.buckets ?? []),
    ];
    const byDay = dayAggMap(buckets, 'Asia/Kolkata', currentSeries.currency);
    currentAgg = windowFromSeriesDays(
      currentSeries.buckets,
      current.isoFrom,
      current.isoTo,
      'Asia/Kolkata',
      currentSeries.currency,
    );
    moneySource = currentSeries.source;
    incomplete = false;
    if (prior && priorSeries) {
      priorAgg = windowFromSeriesDays(
        priorSeries.buckets,
        prior.isoFrom,
        prior.isoTo,
        'Asia/Kolkata',
        priorSeries.currency,
      );
    }
    sparkAggs = sparkDays.map(
      (d) =>
        byDay[d.isoFrom] ??
        windowFromMetrics({
          connection_id: connectionId,
          from: d.isoFrom,
          to: d.isoTo,
          timezone: 'Asia/Kolkata',
          sales: 0,
          order_items: 0,
          units: 0,
          complete: true,
          currency: currentSeries.currency,
        }),
    );
    inventory = inv;
    pricing = pricingPage?.items ?? [];
  } catch (e) {
    error = e instanceof Error ? e.message : 'Failed to load KPI inputs';
    currentAgg = {
      orderCount: 0,
      ordersWithTotal: 0,
      sales: null,
      currencies: [],
      units: null,
      ordersWithUnits: 0,
      unshipped: 0,
      canceled: 0,
      aov: null,
    };
    priorAgg = undefined;
    sparkAggs = [];
    moneySource = undefined;
  }

  const pAgg = priorAgg ?? {
    orderCount: 0,
    ordersWithTotal: 0,
    sales: null,
    currencies: [] as string[],
    units: null,
    ordersWithUnits: 0,
    unshipped: 0,
    canceled: 0,
    aov: null,
  };

  const invZero = inventory.rows.length ? inventory.zero : null;
  const health = buildBusinessHealth({
    yesterday: currentAgg,
    prior: pAgg,
    inventoryZero: invZero,
    inventorySample: inventory.rows.length,
    connectionStatus: conn.status,
    staleHours: staleHours(lastSync),
    ordersQuality: ordersRow?.quality,
    hrefs: {
      orders: `/amazon/orders?connection=${encodeURIComponent(connectionId)}&from=${current.isoFrom}&to=${current.isoTo}`,
      inventory: `/amazon/inventory?connection=${encodeURIComponent(connectionId)}`,
      connect: `/amazon/connect?connection=${encodeURIComponent(connectionId)}`,
    },
  });
  if (incomplete) {
    health.confidence = Math.max(0, health.confidence - 15);
    health.confidenceLabel =
      health.confidence >= 70 ? 'high' : health.confidence >= 40 ? 'medium' : 'low';
  }

  return buildExecutiveKpis({
    connectionId,
    current,
    prior,
    orders: [],
    inventory: inventory.rows,
    pricing,
    health,
    sparkDays,
    truncated: incomplete,
    periodLabel: current.label,
    priorLabel: prior?.label ?? 'No compare',
    error,
    currentAgg,
    priorAgg,
    sparkAggs,
    moneySource,
  });
}
